import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowUpDown, ArrowUp, ArrowDown, TrendingUp, DollarSign, Percent, PieChart, CheckCircle2, AlertTriangle, FileBarChart, Sparkles, Copy } from 'lucide-react';
import TopBar from '../components/Tables/TopBar/TopBar';
import ProjectionsFilters from './ProjectionsFilters';
import {
    FinancialProjectionsViewProps, SortKey,
    formatBillions, formatCurrency, formatPercent, getMethodBadge, getCellTooltip,
} from './projectionsFormat';
import {
    ProjectionRow, StatusFilter, ERREUR_BACKTEST,
    estNonProjete, dividendeAffiche, payoutHorsNorme, fourchette, libelleFiabilite,
} from './projectionsModel';

interface SummaryCardProps {
    title: string;
    value: string;
    subValue: string;
    icon: React.ReactNode;
    accent: 'emerald' | 'blue' | 'purple' | 'orange' | 'indigo' | 'rose';
    hint?: string;
}

const SummaryCard: React.FC<SummaryCardProps> = ({ title, value, subValue, icon, accent, hint }) => (
    <div className={`summary-card accent-${accent}`} title={hint}>
        <div className="summary-card__glow" />
        <div className="summary-card__content">
            <p className="summary-card__title">{title}</p>
            <h3 className="summary-card__value">{value}</h3>
            <div className="summary-card__badge">{subValue}</div>
        </div>
        <div className="summary-card__icon">{icon}</div>
    </div>
);

const STATUS_TABS: { key: StatusFilter; label: string; icon: React.ReactNode; hint: string }[] = [
    { key: 'all',         label: 'Toutes',       icon: <FileBarChart size={14} />,  hint: 'Toutes les sociétés cotées' },
    { key: 'published',   label: 'Publiées',     icon: <CheckCircle2 size={14} />,  hint: "Comptes annuels de l'exercice disponibles" },
    { key: 'estimated',   label: 'Projetées',    icon: <Sparkles size={14} />,      hint: "Estimation à partir d'une publication intermédiaire (S1 ou 9 mois)" },
    { key: 'non_projete', label: 'Non projetées', icon: <Copy size={14} />,         hint: "Aucune publication intermédiaire : chiffres de l'exercice précédent recopiés" },
    { key: 'risk',        label: 'Divergences',  icon: <AlertTriangle size={14} />, hint: 'Les deux méthodes de projection divergent de plus de 25 %' },
];

const pct1 = (v: number) => v.toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

const ecartClass = (v: number) => (Math.abs(v) < 0.15 ? 'cell-ecart--ok' : Math.abs(v) < 0.35 ? 'cell-ecart--warn' : 'cell-ecart--bad');

/** Cellule montant (CA ou résultat) : publié > estimé (avec fourchette) > recopié de N-1. */
const MontantCell: React.FC<{
    row: ProjectionRow;
    label: string;
    est: number | null;
    actual: number | null;
    low: number | null;
    high: number | null;
    method: string | null;
    actualMethod: string | null;
    estVsActual: number | null;
    accent?: string;
}> = ({ row, label, est, actual, low, high, method, actualMethod, estVsActual, accent }) => {
    const tooltip = getCellTooltip(method, actualMethod, estVsActual, est, actual, label, row.expected_mape);
    if (actual) {
        const b = getMethodBadge(actualMethod ?? method);
        return (
            <td className="cell-value cell-value--primary" title={tooltip}>
                <div className="cell-actual-block">
                    <span className="cell-actual-value" style={accent ? { color: accent } : undefined}>{formatBillions(actual)}</span>
                    <div className="cell-badges-row">
                        {b ? <span className={`method-badge ${b.cls}`}>{b.label}</span> : <span className="cell-actual-label">Publié</span>}
                    </div>
                    {estVsActual != null && (
                        <span className={`cell-ecart ${ecartClass(estVsActual)}`}>
                            est. {formatBillions(est)}
                            <span className={estVsActual <= 0 ? 'ecart-sign ecart-sign--up' : 'ecart-sign ecart-sign--down'}>
                                {estVsActual > 0 ? '+' : ''}{pct1(estVsActual * 100)} %
                            </span>
                        </span>
                    )}
                </div>
            </td>
        );
    }
    const b = getMethodBadge(method);
    const f = estNonProjete(row) ? null : fourchette(low, high, method);
    return (
        <td className="cell-value cell-value--primary" title={tooltip}>
            <div className="cell-est-block">
                <span style={estNonProjete(row) ? { color: '#94a3b8' } : undefined}>{formatBillions(est)}</span>
                {b && <span className={`method-badge ${b.cls}`}>{b.label}</span>}
                {f && <span className="cell-range">{formatBillions(f[0])} – {formatBillions(f[1])}</span>}
            </div>
        </td>
    );
};

const DividendeCell: React.FC<{ row: ProjectionRow }> = ({ row }) => {
    const d = dividendeAffiche(row);
    if (d.nature === 'AUCUN') {
        return (
            <td className="cell-value" title="Décision de l'assemblée générale : pas de distribution au titre de cet exercice.">
                <div className="cell-actual-block">
                    <span className="cell-actual-value" style={{ color: '#64748b' }}>0</span>
                    <div className="cell-badges-row"><span className="method-badge method-badge--gray">Pas de dividende</span></div>
                </div>
            </td>
        );
    }
    if (d.nature === 'REEL') {
        return (
            <td className="cell-value cell-value--accent"
                title={`Dividende brut ${d.source === 'ANNONCE' ? 'annoncé' : 'payé'} : ${formatCurrency(d.montant)} F CFA par action. Estimation du modèle : ${formatCurrency(row.div_n_est)}${row.div_est_vs_actual != null ? ` (écart ${pct1(row.div_est_vs_actual * 100)} %)` : ''}`}>
                <div className="cell-actual-block">
                    <span className="cell-actual-value" style={{ color: '#059669' }}>{formatCurrency(d.montant)}</span>
                    <div className="cell-badges-row">
                        <span className={`method-badge ${d.source === 'ANNONCE' ? 'method-badge--amber' : 'method-badge--teal'}`}>
                            {d.source === 'ANNONCE' ? 'Annoncé' : 'Payé'}
                        </span>
                    </div>
                    {row.div_est_vs_actual != null && (
                        <span className={`cell-ecart ${ecartClass(row.div_est_vs_actual)}`}>
                            est. {formatCurrency(row.div_n_est)}
                        </span>
                    )}
                </div>
            </td>
        );
    }
    if (d.nature === 'ESTIME') {
        return (
            <td className="cell-value cell-value--accent"
                title={`Dividende brut estimé par le modèle (croissance historique du dividende, à défaut payout historique × résultat estimé) : ${formatCurrency(d.montant)} F CFA par action`}>
                <div className="cell-est-block">
                    <span style={{ color: estNonProjete(row) ? '#94a3b8' : '#1d4ed8' }}>{formatCurrency(d.montant)}</span>
                </div>
            </td>
        );
    }
    return <td className="cell-value" style={{ color: '#cbd5e1' }}>—</td>;
};

const PayoutCell: React.FC<{ row: ProjectionRow }> = ({ row }) => {
    if (!row.payout_ratio) return <td className="cell-value">—</td>;
    if (payoutHorsNorme(row)) {
        return (
            <td className="cell-value"
                title="Le dividende estimé dépasse le résultat net estimé. Soit le dividende est extrapolé de sa croissance passée alors que le résultat projeté recule, soit la société distribue historiquement plus que son résultat consolidé. À lire avec prudence.">
                <span className="payout-alert"><AlertTriangle size={11} /> {row.payout_ratio.toFixed(0)} %</span>
            </td>
        );
    }
    return <td className="cell-value">{row.payout_ratio.toFixed(0)} %</td>;
};

const FinancialProjectionsDesktop: React.FC<FinancialProjectionsViewProps> = ({
    data, loading, error, cacheDate, annee, annees, onAnneeChange, keyword, setKeyword,
    sortConfig, handleSort, statusFilter, setStatusFilter,
    filters, setFilters, processedData, summaryMetrics,
}) => {
    const navigate = useNavigate();
    const anneePrec = annee != null ? annee - 1 : null;

    const getSortIcon = (key: SortKey) => {
        if (sortConfig.key !== key) return <ArrowUpDown size={12} style={{ marginLeft: 4, opacity: 0.4 }} />;
        return sortConfig.direction === 'asc'
            ? <ArrowUp size={12} style={{ marginLeft: 4, color: '#6366f1' }} />
            : <ArrowDown size={12} style={{ marginLeft: 4, color: '#6366f1' }} />;
    };
    const Th: React.FC<{ k: SortKey; children: React.ReactNode; hint?: string }> = ({ k, children, hint }) => (
        <th onClick={() => handleSort(k)} style={{ cursor: 'pointer' }} title={hint}>{children} {getSortIcon(k)}</th>
    );

    const counts = summaryMetrics?.counts;

    return (
        <div className="projections-container">
            {/* En-tête */}
            <div className="projections-hero">
                <div className="projections-hero__bg" />
                <div className="projections-hero__content">
                    <div className="projections-hero__text-group">
                        <div className="projections-hero__badge">
                            <Sparkles size={12} /> Projections annuelles
                        </div>
                        <h2 className="projections-hero__title">Exercice {annee ?? '—'}</h2>
                        <p className="projections-hero__description">
                            Résultat annuel extrapolé des comptes intermédiaires publiés (premier semestre ou 9 mois) :
                            70 % extrapolation simple, 30 % ancrage sur l'exercice précédent.
                            Erreur moyenne mesurée sur 2020-2024 : chiffre d'affaires {pct1(ERREUR_BACKTEST.caDepuisS1)} % depuis le S1
                            ({pct1(ERREUR_BACKTEST.caDepuis9M)} % depuis 9 mois), résultat net {pct1(ERREUR_BACKTEST.rnDepuisS1)} % depuis le S1
                            ({pct1(ERREUR_BACKTEST.rnDepuis9M)} % depuis 9 mois).
                        </p>
                        {cacheDate && (
                            <p className="projections-hero__cache-date">
                                Calculé le {new Date(cacheDate).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}
                            </p>
                        )}
                    </div>
                    {counts && (
                        <div className="projections-hero__stats">
                            <div className="projections-hero__stat-card" title="Sociétés dont l'estimation repose sur une publication de l'exercice">
                                <div className="projections-hero__stat-label">Projetées</div>
                                <div className="projections-hero__stat-value">{counts.estimated}<span>/ {counts.total}</span></div>
                            </div>
                            <div className="projections-hero__stat-card" title="Comptes annuels de l'exercice déjà publiés">
                                <div className="projections-hero__stat-label">Publiées</div>
                                <div className="projections-hero__stat-value">{counts.published}</div>
                            </div>
                            <div className="projections-hero__stat-card" title="Aucune publication de l'exercice : chiffres de l'exercice précédent recopiés">
                                <div className="projections-hero__stat-label">Non projetées</div>
                                <div className="projections-hero__stat-value">{counts.nonProjete}</div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Synthèse — hors lignes non projetées */}
            {summaryMetrics && (
                <div className="projections-kpi-grid">
                    {summaryMetrics.rendementMax && (
                        <SummaryCard title="Rendement estimé le plus haut" value={`${pct1(summaryMetrics.rendementMax.yield_est as number)} %`}
                            subValue={summaryMetrics.rendementMax.symbol} icon={<Percent size={18} />} accent="emerald"
                            hint="Dividende brut estimé / cours actuel. Sociétés non projetées et divergentes exclues." />
                    )}
                    {summaryMetrics.perMin && (
                        <SummaryCard title="PER estimé le plus bas" value={`${pct1(summaryMetrics.perMin.per_est as number)}x`}
                            subValue={summaryMetrics.perMin.symbol} icon={<DollarSign size={18} />} accent="blue"
                            hint="Cours actuel / bénéfice par action estimé. Sociétés non projetées et divergentes exclues." />
                    )}
                    {summaryMetrics.croissanceMax && summaryMetrics.croissanceMax.var_net_income != null && (
                        <SummaryCard title="Plus forte hausse du résultat estimé"
                            value={`${summaryMetrics.croissanceMax.var_net_income > 0 ? '+' : ''}${(summaryMetrics.croissanceMax.var_net_income * 100).toFixed(0)} %`}
                            subValue={summaryMetrics.croissanceMax.symbol} icon={<TrendingUp size={18} />} accent="purple"
                            hint="Sociétés projetées, annuel non publié, hors divergences." />
                    )}
                    {summaryMetrics.payoutMedian != null && (
                        <SummaryCard title="Payout médian estimé" value={`${summaryMetrics.payoutMedian.toFixed(0)} %`}
                            subValue="Hors payout > 100 %" icon={<PieChart size={18} />} accent="orange" />
                    )}
                    <SummaryCard title="Annuels publiés" value={`${summaryMetrics.counts.published}/${summaryMetrics.counts.total}`}
                        subValue={`Exercice ${annee ?? ''}`} icon={<CheckCircle2 size={18} />} accent="indigo" />
                    <SummaryCard title="Divergences" value={`${summaryMetrics.counts.risk}`}
                        subValue="Risque de retour à la moyenne" icon={<AlertTriangle size={18} />} accent="rose" />
                </div>
            )}

            <ProjectionsFilters
                filters={filters}
                onFiltersChange={setFilters}
                annees={annees}
                annee={annee}
                onAnneeChange={onAnneeChange}
                totalResults={data.length}
                filteredResults={processedData.length}
            />

            <div className="status-tabs-container">
                <div className="status-chips">
                    {STATUS_TABS.map(tab => {
                        const count = !counts ? null
                            : tab.key === 'all' ? counts.total
                            : tab.key === 'published' ? counts.published
                            : tab.key === 'estimated' ? counts.estimated
                            : tab.key === 'non_projete' ? counts.nonProjete
                            : counts.risk;
                        const active = statusFilter === tab.key;
                        return (
                            <button key={tab.key} data-active={active} onClick={() => setStatusFilter(tab.key)} className="status-chip" title={tab.hint}>
                                {tab.icon}
                                <span>{tab.label}</span>
                                {count != null && <span className="status-chip__count">{count}</span>}
                            </button>
                        );
                    })}
                </div>
                <div style={{ flex: 1 }}>
                    <TopBar
                        searchValue={keyword}
                        searchOnChange={(e) => setKeyword(e.target.value)}
                        searchSubmit={(e) => e.preventDefault()}
                    />
                </div>
            </div>

            {loading ? (
                <div className="projections-table-box">
                    <div className="skeleton-header" />
                    {[...Array(9)].map((_, i) => (
                        <div key={i} className="skeleton-row">
                            <div className="skeleton-cell skeleton-cell--logo" />
                            <div className="skeleton-cell skeleton-cell--name" />
                            <div style={{ flex: 1 }} />
                            <div className="skeleton-cell skeleton-cell--num" />
                            <div className="skeleton-cell skeleton-cell--num" />
                            <div className="skeleton-cell skeleton-cell--wide" />
                            <div className="skeleton-cell skeleton-cell--num" />
                            <div className="skeleton-cell skeleton-cell--wide" />
                        </div>
                    ))}
                </div>
            ) : error ? (
                <div className="projections-table-box" style={{ padding: '40px', textAlign: 'center', color: '#f43f5e' }}>{error}</div>
            ) : data.length === 0 ? (
                <div className="projections-table-box" style={{ padding: '48px', textAlign: 'center' }}>
                    <p style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>
                        Aucune projection calculée{annee ? ` pour ${annee}` : ''}
                    </p>
                    <p style={{ fontSize: 13, color: '#94a3b8' }}>
                        Lancez le calcul depuis <strong>Admin → Contrôle Azure Functions → Projection Cache</strong>
                    </p>
                </div>
            ) : (
                <div className="projections-table-box">
                    <div className="data-table-wrapper">
                        <table className="premium-table">
                            <thead>
                                <tr>
                                    <th>Société</th>
                                    <Th k="yield_est" hint="Dividende brut estimé / cours actuel">REND. EST.</Th>
                                    <Th k="per_est" hint="Cours actuel / bénéfice par action estimé">PER EST.</Th>
                                    <Th k="revenue_n_est">CA {annee}</Th>
                                    <Th k="net_margin">MARGE EST.</Th>
                                    <th>RÉS. {anneePrec}</th>
                                    <Th k="net_income_n_est">RÉS. {annee}</Th>
                                    <Th k="var_net_income">VAR. RÉS.</Th>
                                    <th>DIV. {anneePrec}</th>
                                    <Th k="div_n_est">DIV. {annee}</Th>
                                    <Th k="var_div">VAR. DIV.</Th>
                                    <Th k="payout_ratio" hint="Dividende estimé × nombre de titres / résultat net estimé">PAYOUT EST.</Th>
                                </tr>
                            </thead>
                            <tbody>
                                {processedData.map((row) => {
                                    const nonProjete = estNonProjete(row);
                                    return (
                                        <tr key={row.symbol} className={nonProjete ? 'row-non-projete' : undefined}>
                                            <td>
                                                <div className="company-cell" onClick={() => navigate(`/data?symbol=${row.symbol}`)} style={{ cursor: 'pointer' }}>
                                                    <div className="company-logo-container">
                                                        <img
                                                            src={`/images/societes/${row.symbol}.png`}
                                                            alt={row.symbol}
                                                            className="company-logo"
                                                            onError={(e) => {
                                                                (e.target as HTMLImageElement).style.display = 'none';
                                                                const fallback = (e.target as HTMLImageElement).nextElementSibling as HTMLElement;
                                                                if (fallback) fallback.style.display = 'flex';
                                                            }}
                                                        />
                                                        <div className="company-logo-fallback" style={{ display: 'none' }}>
                                                            {row.symbol.substring(0, 3)}
                                                        </div>
                                                    </div>
                                                    <div className="company-info">
                                                        <span className="company-symbol">{row.symbol}</span>
                                                        <div className="company-badges">
                                                            {nonProjete ? (
                                                                <span className="reliability-badge reliability-badge--none" title="Aucune publication de l'exercice : chiffres de l'exercice précédent recopiés">
                                                                    Non projeté
                                                                </span>
                                                            ) : row.reliability_tier && (
                                                                <span
                                                                    className={`reliability-badge reliability-badge--${row.reliability_tier === 'FIABLE' ? 'high' : row.reliability_tier === 'MOYEN' ? 'med' : 'low'}`}
                                                                    title={row.expected_mape != null ? `Erreur attendue pour ce secteur : ±${Math.round(row.expected_mape * 100)} %` : undefined}
                                                                >
                                                                    {libelleFiabilite(row.reliability_tier)}
                                                                </span>
                                                            )}
                                                            {row.growth_capped && (
                                                                <span className="risk-tag" title="Les deux méthodes divergent de plus de 25 % : risque de retour à la moyenne">
                                                                    <AlertTriangle size={10} /> Divergence
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="cell-value cell-value--yield">{row.yield_est ? `${pct1(row.yield_est)} %` : '—'}</td>
                                            <td className="cell-value">{row.per_est ? `${pct1(row.per_est)}x` : '—'}</td>
                                            <MontantCell row={row} label="Chiffre d'affaires" est={row.revenue_n_est} actual={row.revenue_n_actual}
                                                low={row.revenue_n_est_low} high={row.revenue_n_est_high} method={row.revenue_method}
                                                actualMethod={row.revenue_actual_method} estVsActual={row.revenue_est_vs_actual} />
                                            <td className="cell-value">{row.net_margin ? `${pct1(row.net_margin)} %` : '—'}</td>
                                            <td className="cell-value" style={{ color: '#94a3b8' }}>{formatBillions(row.net_income_n_1)}</td>
                                            <MontantCell row={row} label="Résultat net" est={row.net_income_n_est} actual={row.net_income_n_actual}
                                                low={row.net_income_n_est_low} high={row.net_income_n_est_high} method={row.net_income_method}
                                                actualMethod={row.net_income_actual_method} estVsActual={row.net_income_est_vs_actual} accent="#4f46e5" />
                                            <td>{nonProjete ? formatPercent(null) : formatPercent(row.var_net_income)}</td>
                                            <td className="cell-value" style={{ color: '#94a3b8' }}>{formatCurrency(row.div_n_1)}</td>
                                            <DividendeCell row={row} />
                                            <td>{dividendeAffiche(row).nature === 'AUCUN' ? formatPercent(null) : formatPercent(row.var_div)}</td>
                                            <PayoutCell row={row} />
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <div className="projections-legend">
                        <div className="legend-item"><span className="reliability-badge reliability-badge--high" style={{ width: 12, height: 12 }}></span> Fiable : erreur sectorielle attendue &lt; 20 %</div>
                        <div className="legend-item"><span className="reliability-badge reliability-badge--med" style={{ width: 12, height: 12 }}></span> Moyen : 20 à 40 %</div>
                        <div className="legend-item"><span className="reliability-badge reliability-badge--low" style={{ width: 12, height: 12 }}></span> Non fiable : au-delà</div>
                        <div className="legend-item" style={{ color: '#f59e0b' }}><AlertTriangle size={12} /> Divergence ou payout &gt; 100 % : à lire avec prudence</div>
                        <div style={{ marginLeft: 'auto', fontStyle: 'italic', opacity: 0.7 }}>Montants en F CFA, dividendes bruts par action</div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FinancialProjectionsDesktop;
